import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { ApiService } from 'src/app/shared';
import { initGatewayTypes } from '../credit-card-gateway/credit-card-gateway.service';

/**
 * Internal normalised (camelCase) shape. Built by toPaymentTypeFields from
 * either a legacy PaymentType/{id} record or an entry of
 * GET financial/v2/revenue/GetDefaultPaymentTypesForRevenueItems, which is
 * PascalCase on the real wire (camelCase appears only in older fixtures).
 */
interface PaymentTypeFields {
  accountingPackageId?: number | null;
  paymentTypeId?: number | null;
  /** Payment type display name */
  paymentType?: string | null;
  creditCardType?: boolean | null;
  achType?: boolean | null;
  ccGatewayId?: number | null;
  ccGatewayTypeId?: number | null;
  /** Gateway display name — not yet emitted by the API; the node falls
   *  back to `Gateway #${ccGatewayId}` when absent. */
  ccGatewayName?: string | null;
}

/** Payment type as it arrives on the wire: legacy PaymentType/{id} (PascalCase, two observed variants)
 *  or an item-response entry (PascalCase on the real wire; camelCase in older fixtures). Only fields read are declared. */
interface PaymentTypeWire {
  PaymentTypeId?: number | null; PaymentTypeID?: number | null; paymentTypeId?: number | null;
  PaymentType?: string | null; paymentType?: string | null;
  CreditCardType?: boolean | null; creditCardType?: boolean | null;
  AchType?: boolean | null; ACHType?: boolean | null; achType?: boolean | null;
  AccountingPackageId?: number | null; AccountingPackageID?: number | null; accountingPackageId?: number | null;
  CcGatewayId?: number | null; CCGatewayID?: number | null; ccGatewayId?: number | null;
  CcGatewayTypeId?: number | null; ccGatewayTypeId?: number | null;
  ccGatewayName?: string | null;
}

export interface PaymentTypeMapDto {
  PaymentType?: PaymentTypeWire | null;
  paymentType?: PaymentTypeWire | null;
  ItemNums?: number[];
  itemNums?: number[];
}

interface AccountingPackageSummary {
  accountingPackageId?: number | null;
  name?: string | null;
  paymentTypeId?: number | null;
}

interface GatewayDetail {
  ccGatewayId: number;
  name?: string | null;
  gatewayTypeId?: number | null;
  gatewayTypeName?: string | null;
  isSandbox?: boolean | null;
  precznMerchantId?: string | null;
  precznProcessorType?: number | null;
  precznECheck?: boolean | null;
  cardConnectMerchId?: string | null;
}

/** Legacy wire records (PascalCase). Only the fields read are declared. */
interface LegacyAccountingPackageRecord {
  AccountingPackageId?: number;
  AccountingPackageID?: number;
  Name?: string;
  PaymentTypeID?: number;
  PaymentTypeId?: number;
}

interface LegacyGatewayRecord {
  CCGatewayID?: number;
  CcGatewayId?: number;
  Name?: string;
  GatewayTypeID?: number;
  GatewayTypeId?: number;
  IsSandbox?: boolean;
  PrecznMerchantId?: string;
  PrecznProcessorType?: number;
  PrecznECheck?: boolean;
  CardConnectMerchId?: string;
}

type GatewayTreeScope =
  | { kind: 'item'; id: number }
  | { kind: 'accounting-package'; id: number }
  | { kind: 'payment-type'; id: number }
  | { kind: 'gateway'; id: number };

const GATEWAY_TYPES = initGatewayTypes();

@Component({
  selector: 'wl-gateway-resolution-tree',
  templateUrl: './gateway-resolution-tree.component.html',
  styleUrls: ['./gateway-resolution-tree.component.less'],
  standalone: false,
})
export class GatewayResolutionTreeComponent implements OnInit, OnChanges {
  /** Scope inputs. Root-most set input wins: itemNum, accountingPackageId, paymentTypeId, ccGatewayId. */
  @Input() itemNum?: number | null;
  @Input() accountingPackageId?: number | null;
  @Input() paymentTypeId?: number | null;
  @Input() ccGatewayId?: number | null;

  /** Optional display label for the revenue-item node.
   *  Defaults to "Item #{itemNum}" when not supplied. */
  @Input() itemDescription?: string;

  /** Optional display label for the accounting-package node.
   *  Defaults to "Package #{accountingPackageId}" when not supplied. */
  @Input() accountingPackageName?: string;

  public treeNodes: TreeNode[] = [];
  public isLoading = false;
  public loadError = false;
  public showDialog = false;
  public selectedNode: TreeNode | null = null;

  private _loadSeq = 0;

  constructor(private _apiService: ApiService) {}

  ngOnInit(): void {
    void this.loadTree();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const keys = ['itemNum', 'accountingPackageId', 'paymentTypeId', 'ccGatewayId'];
    const reload = keys.some((k) => changes[k] && !changes[k].firstChange);
    if (reload) {
      this.closeDialog();
      void this.loadTree();
    }
  }

  private resolveScope(): GatewayTreeScope | null {
    if (this.itemNum != null) {
      return { kind: 'item', id: this.itemNum };
    }
    if (this.accountingPackageId != null) {
      return { kind: 'accounting-package', id: this.accountingPackageId };
    }
    if (this.paymentTypeId != null) {
      return { kind: 'payment-type', id: this.paymentTypeId };
    }
    if (this.ccGatewayId != null) {
      return { kind: 'gateway', id: this.ccGatewayId };
    }
    return null;
  }

  private isLinked(id: number | null | undefined): id is number {
    return typeof id === 'number' && id > 0;
  }

  private loadTree(): Promise<void> {
    const scope = this.resolveScope();
    const seq = ++this._loadSeq;
    this.loadError = false;
    if (!scope) {
      this.treeNodes = [];
      this.isLoading = false;
      return Promise.resolve();
    }
    this.isLoading = true;
    return this.fetchNodes(scope)
      .then((nodes) => {
        if (seq === this._loadSeq) {
          this.treeNodes = nodes;
        }
      })
      .catch(() => {
        if (seq === this._loadSeq) {
          this.treeNodes = [];
          this.loadError = true;
        }
      })
      .finally(() => {
        if (seq === this._loadSeq) {
          this.isLoading = false;
        }
      });
  }

  private async fetchNodes(scope: GatewayTreeScope): Promise<TreeNode[]> {
    switch (scope.kind) {
      case 'item': {
        // Use the financial service's own base URL when configured; fall back
        // to the tenant WebApiUrl otherwise (the API gateway proxies it).
        const url = this.financialUrl(
          `financial/v2/revenue/GetDefaultPaymentTypesForRevenueItems?itemNums=${scope.id}`
        );
        const response: PaymentTypeMapDto[] | null | undefined = await this._apiService.get(url, undefined, false);
        if (response == null) {
          return [];
        }
        if (!Array.isArray(response)) {
          throw new Error('Item payment types unavailable');
        }
        const entries = response.map((m) => this.toItemEntry(m));
        const ptIds: number[] = [];
        const apIds: number[] = [];
        for (const e of entries) {
          if (this.isLinked(e.paymentTypeId) && !ptIds.includes(e.paymentTypeId)) {
            ptIds.push(e.paymentTypeId);
          }
          if (this.isLinked(e.accountingPackageId) && !apIds.includes(e.accountingPackageId)) {
            apIds.push(e.accountingPackageId);
          }
        }
        const [ptList, apList] = await Promise.all([
          Promise.all(ptIds.map((id) => this.getPaymentTypeRecord(id))),
          Promise.all(apIds.map((id) => this.getAccountingPackageRecord(id))),
        ]);
        const ptRecs = new Map<number, PaymentTypeFields>();
        ptIds.forEach((id, i) => ptRecs.set(id, ptList[i]));
        const apRecs = new Map<number, AccountingPackageSummary>();
        apIds.forEach((id, i) => apRecs.set(id, apList[i]));
        const gwIds: number[] = [];
        for (const e of entries) {
          if (!this.isLinked(e.paymentTypeId)) {
            continue;
          }
          const gid = this.mergePaymentType(ptRecs.get(e.paymentTypeId), e).ccGatewayId;
          if (this.isLinked(gid) && !gwIds.includes(gid)) {
            gwIds.push(gid);
          }
        }
        const gateways = gwIds.length ? await this.getGatewayMap(gwIds) : undefined;
        return this.buildTreeNodes(response, gateways, ptRecs, apRecs);
      }
      case 'accounting-package': {
        const ap = await this.getAccountingPackageRecord(scope.id);
        let ptNode: TreeNode | undefined;
        if (this.isLinked(ap.paymentTypeId)) {
          ptNode = await this.buildPaymentTypeChain(ap.paymentTypeId);
        }
        return [this.buildAccountingPackageNode(ap.accountingPackageId ?? scope.id, ap.name || this.accountingPackageName, ap.paymentTypeId, ptNode)];
      }
      case 'payment-type':
        return [await this.buildPaymentTypeChain(scope.id)];
      case 'gateway': {
        const gateways = await this.getGatewayMap([scope.id]);
        return [this.buildGatewayNode(scope.id, gateways.get(scope.id))];
      }
    }
  }

  private async buildPaymentTypeChain(paymentTypeId: number): Promise<TreeNode> {
    const pt = await this.getPaymentTypeRecord(paymentTypeId);
    let gwNode: TreeNode | undefined;
    if (this.isLinked(pt.ccGatewayId)) {
      const gateways = await this.getGatewayMap([pt.ccGatewayId]);
      gwNode = this.buildGatewayNode(pt.ccGatewayId, gateways.get(pt.ccGatewayId));
    }
    return this.buildPaymentTypeNode(pt, gwNode);
  }

  private financialUrl(path: string): string {
    const base = this._apiService.getServiceBaseUrl('financial');
    return base ? `${base}/${path}` : path;
  }

  private async getPaymentTypeRecord(id: number): Promise<PaymentTypeFields> {
    const raw = await this._apiService.get('PaymentType/' + id, undefined, false);
    if (raw == null || typeof raw !== 'object') {
      throw new Error('Payment type not found');
    }
    return this.toPaymentTypeFields(raw as PaymentTypeWire);
  }

  private async getAccountingPackageRecord(id: number): Promise<AccountingPackageSummary> {
    const raw = await this._apiService.get('AccountingPackage/' + id, undefined, false);
    if (raw == null || typeof raw !== 'object') {
      throw new Error('Accounting package not found');
    }
    return this.toAccountingPackageSummary(raw as LegacyAccountingPackageRecord);
  }

  private async getGatewayMap(ids: number[]): Promise<Map<number, GatewayDetail>> {
    const raw = await this._apiService.get('CreditCardGateways', undefined, false);
    if (!Array.isArray(raw)) {
      throw new Error('Gateway list unavailable');
    }
    const map = new Map<number, GatewayDetail>();
    for (const r of raw as LegacyGatewayRecord[]) {
      if (r == null || typeof r !== 'object') {
        continue;
      }
      const detail = this.toGatewayDetail(r);
      if (detail.ccGatewayId != null) {
        map.set(detail.ccGatewayId, detail);
      }
    }
    for (const id of ids) {
      if (!map.has(id)) {
        throw new Error(`Gateway ${id} not found`);
      }
    }
    return map;
  }

  private toPaymentTypeFields(raw: PaymentTypeWire): PaymentTypeFields {
    return {
      paymentTypeId: raw.PaymentTypeId ?? raw.PaymentTypeID ?? raw.paymentTypeId,
      paymentType: raw.PaymentType ?? raw.paymentType,
      creditCardType: raw.CreditCardType ?? raw.creditCardType,
      achType: raw.AchType ?? raw.ACHType ?? raw.achType,
      accountingPackageId: raw.AccountingPackageId ?? raw.AccountingPackageID ?? raw.accountingPackageId,
      ccGatewayId: raw.CcGatewayId ?? raw.CCGatewayID ?? raw.ccGatewayId,
      ccGatewayTypeId: raw.CcGatewayTypeId ?? raw.ccGatewayTypeId,
      ccGatewayName: raw.ccGatewayName,
    };
  }

  private toItemEntry(map: PaymentTypeMapDto | null | undefined): PaymentTypeFields {
    const raw = map?.PaymentType ?? map?.paymentType;
    return this.toPaymentTypeFields(raw && typeof raw === 'object' ? raw : {});
  }

  /** The legacy record wins per field; the item-response entry fills gaps. */
  private mergePaymentType(record: PaymentTypeFields | undefined, entry: PaymentTypeFields): PaymentTypeFields {
    return {
      paymentTypeId: record?.paymentTypeId ?? entry.paymentTypeId,
      paymentType: record?.paymentType ?? entry.paymentType,
      creditCardType: record?.creditCardType ?? entry.creditCardType,
      achType: record?.achType ?? entry.achType,
      accountingPackageId: record?.accountingPackageId ?? entry.accountingPackageId,
      ccGatewayId: record?.ccGatewayId ?? entry.ccGatewayId,
      ccGatewayTypeId: entry.ccGatewayTypeId,
      ccGatewayName: entry.ccGatewayName,
    };
  }

  private toAccountingPackageSummary(raw: LegacyAccountingPackageRecord): AccountingPackageSummary {
    return {
      accountingPackageId: raw.AccountingPackageId ?? raw.AccountingPackageID,
      name: raw.Name,
      paymentTypeId: raw.PaymentTypeID ?? raw.PaymentTypeId,
    };
  }

  private toGatewayDetail(raw: LegacyGatewayRecord): GatewayDetail {
    const gatewayTypeId = raw.GatewayTypeID ?? raw.GatewayTypeId;
    return {
      ccGatewayId: (raw.CCGatewayID ?? raw.CcGatewayId) as number,
      name: raw.Name,
      gatewayTypeId,
      gatewayTypeName: GATEWAY_TYPES.find((t) => t.GatewayTypeId === gatewayTypeId)?.GatewayTypeName ?? null,
      isSandbox: raw.IsSandbox,
      precznMerchantId: raw.PrecznMerchantId,
      precznProcessorType: raw.PrecznProcessorType,
      precznECheck: raw.PrecznECheck,
      cardConnectMerchId: raw.CardConnectMerchId,
    };
  }

  private buildGatewayNode(
    ccGatewayId: number,
    detail?: GatewayDetail | null,
    fallbackTypeId?: number | null,
    fallbackName?: string | null
  ): TreeNode {
    const name = detail?.name ?? fallbackName;
    return {
      type: 'gateway',
      icon: 'pi pi-cloud',
      label: detail?.name || fallbackName || `Gateway #${ccGatewayId}`,
      expanded: true,
      data: {
        id: ccGatewayId,
        name,
        gatewayTypeId: detail?.gatewayTypeId ?? fallbackTypeId,
        gatewayTypeName: detail?.gatewayTypeName,
        isSandbox: detail?.isSandbox,
        precznMerchantId: detail?.precznMerchantId,
        precznProcessorType: detail?.precznProcessorType,
        precznECheck: detail?.precznECheck,
        cardConnectMerchId: detail?.cardConnectMerchId,
        tooltip: name ? `ID: ${ccGatewayId} | ${name}` : `ID: ${ccGatewayId}`,
      },
      children: [],
    };
  }

  private buildPaymentTypeNode(pt: PaymentTypeFields, gateway?: TreeNode): TreeNode {
    return {
      type: 'payment-type',
      icon: 'pi pi-credit-card',
      label: pt.paymentType || (pt.paymentTypeId != null ? `Payment Type #${pt.paymentTypeId}` : 'Unknown'),
      expanded: true,
      data: {
        id: pt.paymentTypeId,
        paymentTypeId: pt.paymentTypeId,
        name: pt.paymentType,
        paymentType: pt.paymentType,
        isCC: pt.creditCardType,
        isACH: pt.achType,
        ccGatewayId: pt.ccGatewayId,
        accountingPackageId: pt.accountingPackageId,
        tooltip: pt.paymentType ? `ID: ${pt.paymentTypeId} | ${pt.paymentType}` : `ID: ${pt.paymentTypeId}`,
      },
      children: gateway ? [gateway] : [],
    };
  }

  private buildAccountingPackageNode(
    id: number | null | undefined,
    name: string | null | undefined,
    paymentTypeId: number | null | undefined,
    child?: TreeNode
  ): TreeNode {
    return {
      type: 'accounting-package',
      icon: 'pi pi-folder',
      label: name || `Package #${id}`,
      expanded: true,
      data: {
        id,
        name,
        paymentTypeId,
        tooltip: `ID: ${id}`,
      },
      children: child ? [child] : [],
    };
  }

  private buildTreeNodes(
    response: PaymentTypeMapDto[],
    gateways?: Map<number, GatewayDetail>,
    paymentTypes?: Map<number, PaymentTypeFields>,
    accountingPackages?: Map<number, AccountingPackageSummary>
  ): TreeNode[] {
    return response.map((map) => {
      const entry = this.toItemEntry(map);
      const ptId = entry.paymentTypeId;
      const apId = entry.accountingPackageId;
      const pt = this.isLinked(ptId) ? this.mergePaymentType(paymentTypes?.get(ptId), entry) : undefined;
      const gwNode = pt?.ccGatewayId
        ? this.buildGatewayNode(pt.ccGatewayId, gateways?.get(pt.ccGatewayId), pt.ccGatewayTypeId, pt.ccGatewayName)
        : undefined;
      const ptNode = pt ? this.buildPaymentTypeNode(pt, gwNode) : undefined;
      const apNode = this.isLinked(apId)
        ? this.buildAccountingPackageNode(
            apId,
            accountingPackages?.get(apId)?.name || this.accountingPackageName,
            pt?.paymentTypeId,
            ptNode
          )
        : undefined;
      return {
        type: 'revenue-item',
        icon: 'pi pi-tag',
        label: this.itemDescription || `Item #${this.itemNum}`,
        expanded: true,
        data: {
          id: this.itemNum,
          description: this.itemDescription,
          accountingPackageId: this.isLinked(apId) ? apId : undefined,
          tooltip: `ID: ${this.itemNum}`,
        },
        children: apNode ? [apNode] : ptNode ? [ptNode] : [],
      };
    });
  }

  onNodeClick(node: TreeNode): void {
    this.selectedNode = node;
    this.showDialog = true;
  }

  closeDialog(): void {
    this.showDialog = false;
    this.selectedNode = null;
  }
}
